// Meetings section (design decisions #2, #8) — re-hosted inside VisitDetail's
// "Meetings" Card (DO-2298 pivot: a meeting always belongs to a visit now). One-click
// record → pipeline → audit. This component is a pure view over the `meetings` array
// VisitDetail already owns (fetched + polled via GET /visits/detail) plus the recorder
// and the audit editor — it no longer fetches its own list, so there's a single poll
// loop (VisitDetail's), not two racing ones.
import { useCallback, useEffect, useState } from "react";
import { Button, Icon, Badge, EmptyState } from "../../design/ui";
import type { Meeting } from "../../lib/brain-api";
import { useMeetingRecorder, unfinishedMeetings, discardBuffered } from "./recorder";
import { MeetingAudit } from "./MeetingAudit";

/** Meeting statuses where the pipeline is still working — VisitDetail polls
 *  GET /visits/detail on a 15s interval while any meeting is in one of these. */
export const MEETING_PROCESSING_STATUSES = new Set(["recording", "uploading", "transcribing", "synthesizing"]);

const STATUS_TONE: Record<string, "info" | "success" | "warning" | "danger" | "neutral"> = {
  recording: "warning", uploading: "warning", transcribing: "info",
  synthesizing: "info", ready: "warning", audited: "success", error: "danger",
};
const STATUS_LABEL: Record<string, string> = {
  recording: "recording", uploading: "uploading", transcribing: "transcribing",
  synthesizing: "writing notes", ready: "ready to audit", audited: "audited", error: "error",
};

function fmtElapsed(sec: number): string {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
const parseAttendees = (s: string): string[] => s.split(",").map((x) => x.trim()).filter(Boolean);

const M_CSS = `
.m-rec-dot { width: 10px; height: 10px; border-radius: 50%; background: var(--kt-danger, #b3261e); display: inline-block; animation: m-pulse 1.4s ease-in-out infinite; }
@keyframes m-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
.m-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid var(--kt-border, #e3e5e8); border-radius: 8px; cursor: pointer; background: var(--kt-surface, #fff); }
.m-row:hover { border-color: var(--kt-teal, #008e92); }
`;

export function CustomerMeetingsTab({ customerId, visitId, meetings, onChanged }: {
  customerId: string;
  visitId: string;
  meetings: Meeting[];
  /** Notify the parent (VisitDetail) so its /visits/detail poll re-fetches. */
  onChanged: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [attendees, setAttendees] = useState("");
  const [resumable, setResumable] = useState<{ meetingId: string; customerId: string; visitId: string; chunks: number }[]>([]);

  const refreshResumable = useCallback(async () => {
    setResumable((await unfinishedMeetings()).filter((r) => r.customerId === customerId && r.visitId === visitId));
  }, [customerId, visitId]);

  useEffect(() => {
    void refreshResumable();
  }, [refreshResumable]);

  const rec = useMeetingRecorder(() => {
    void refreshResumable();
    onChanged();
  });

  if (selected) {
    return (
      <>
        <style>{M_CSS}</style>
        <MeetingAudit
          customerId={customerId}
          visitId={visitId}
          meetingId={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            onChanged();
          }}
        />
      </>
    );
  }

  const recording = rec.status === "recording" || rec.status === "paused";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <style>{M_CSS}</style>

      {/* Resume banner — chunks from a crashed/backgrounded recording survived. */}
      {resumable.map((r) => (
        <div key={r.meetingId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid var(--kt-warning, #b26a00)", borderRadius: 8, flexWrap: "wrap" }}>
          <Icon name="alert-triangle" size={15} color="var(--kt-warning, #b26a00)" />
          <span style={{ fontSize: 13 }}>Unfinished recording found ({r.chunks} chunk(s) buffered).</span>
          <Button size="sm" icon="cloud-upload" onClick={() => void rec.resumeBuffered(r.meetingId, r.customerId, r.visitId).then(refreshResumable)}>
            Finish upload
          </Button>
          <Button size="sm" variant="danger" icon="trash" onClick={() => void discardBuffered(r.meetingId).then(refreshResumable)}>
            Discard
          </Button>
        </div>
      ))}

      {/* Recorder panel */}
      <div style={{ border: "1px solid var(--kt-border, #e3e5e8)", borderRadius: 8, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {!recording && rec.status !== "finalizing" ? (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input className="kt-input" placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} style={{ flex: "1 1 220px" }} />
              <input className="kt-input" placeholder="Who's here? (comma-separated, optional)" value={attendees} onChange={(e) => setAttendees(e.target.value)} style={{ flex: "1 1 260px" }} />
            </div>
            <div>
              <Button variant="primary" icon="microphone" onClick={() => void rec.start({ customerId, visitId, title: title.trim() || undefined, attendees: parseAttendees(attendees) })}>
                Record meeting
              </Button>
            </div>
          </>
        ) : rec.status === "finalizing" ? (
          <div style={{ color: "var(--kt-text-muted)" }}><Icon name="loader-2" size={15} /> Finalizing & uploading…</div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
              <span className="m-rec-dot" /> {rec.status === "paused" ? "PAUSED" : "REC"}
            </span>
            <span style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 16 }}>{fmtElapsed(rec.elapsedSec)}</span>
            <span style={{ fontSize: 12, color: "var(--kt-text-muted)" }}>
              uploaded {rec.uploaded}{rec.buffered > 0 ? ` · ${rec.buffered} buffered` : ""}
            </span>
            {rec.status === "recording"
              ? <Button size="sm" icon="player-pause" onClick={rec.pause}>Pause</Button>
              : <Button size="sm" icon="player-play" onClick={rec.resume}>Resume</Button>}
            <Button size="sm" variant="primary" icon="player-stop" onClick={() => void rec.stop({ title: title.trim() || undefined, attendees: parseAttendees(attendees) })}>
              Stop &amp; process
            </Button>
          </div>
        )}
        {rec.error && <div style={{ fontSize: 13, color: "var(--kt-danger, #b3261e)" }}>{rec.error}</div>}
      </div>

      {/* Meeting list */}
      {meetings.length === 0 ? (
        <EmptyState icon="microphone" title="No meetings yet">
          Hit <strong>Record meeting</strong> during this site visit, or a Google Meet call will appear here once imported.
        </EmptyState>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {meetings.map((m) => (
            <div key={m.id} className="m-row" onClick={() => setSelected(m.id)}>
              <Icon name={m.source === "onsite" ? "microphone" : "video"} size={16} color="var(--kt-text-muted)" />
              <span style={{ fontSize: 12, color: "var(--kt-text-muted)", width: 90 }}>{m.date}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.title}</span>
              <Badge tone={STATUS_TONE[m.status] ?? "neutral"}>{STATUS_LABEL[m.status] ?? m.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
